import { createRootRouteWithContext, Outlet, Link } from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';

interface RouterContext {
  queryClient: QueryClient;
}

/**
 * 404-Seite im Dashboard-Stil.
 * Schlicht, dunkler Hintergrund, großes Akzent-"404", klarer CTA zurück zum Dashboard.
 */
function NotFoundPage() {
  return (
    <>
      <style>{`
        @keyframes dashNfFadeUp {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes dashNfStripe {
          0%   { background-position: 0% 0%; }
          50%  { background-position: 100% 0%; }
          100% { background-position: 0% 0%; }
        }
        .dash-nf-stripe {
          height: 3px;
          background: linear-gradient(90deg, #dc2626 0%, #ef4444 50%, #dc2626 100%);
          background-size: 200% 100%;
          animation: dashNfStripe 6s ease infinite;
        }
        .dash-nf-eyebrow {
          animation: dashNfFadeUp 0.5s ease-out 0.1s forwards;
          opacity: 0;
        }
        .dash-nf-404 {
          animation: dashNfFadeUp 0.6s ease-out 0.2s forwards;
          opacity: 0;
        }
        .dash-nf-headline {
          animation: dashNfFadeUp 0.6s ease-out 0.35s forwards;
          opacity: 0;
        }
        .dash-nf-body {
          animation: dashNfFadeUp 0.6s ease-out 0.5s forwards;
          opacity: 0;
        }
        .dash-nf-cta {
          animation: dashNfFadeUp 0.6s ease-out 0.65s forwards;
          opacity: 0;
        }
        .dash-nf-btn {
          transition: background-color 0.15s ease, transform 0.15s ease;
        }
        .dash-nf-btn:hover {
          transform: translateY(-1px);
        }
      `}</style>

      {/* Akzent-Stripe oben */}
      <div className="dash-nf-stripe fixed top-0 left-0 right-0" aria-hidden="true" />

      <div
        className="flex min-h-screen w-full flex-col items-center justify-center gap-8 px-4 text-center"
        style={{ background: 'var(--color-paper)' }}
      >
        {/* Logo + App-Name */}
        <div className="dash-nf-eyebrow flex flex-col items-center gap-3">
          <img
            src="/logos/fan-logo-white.svg"
            alt="Faninitiative Spandau"
            className="h-10 w-auto opacity-40"
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = 'none';
            }}
          />
          <span
            className="text-[10px] font-black uppercase tracking-[0.3em]"
            style={{ color: 'var(--color-accent)' }}
          >
            Admin-Dashboard
          </span>
        </div>

        {/* Trennlinie */}
        <div
          className="h-px w-16"
          style={{ background: 'rgba(240,238,235,0.08)' }}
          aria-hidden="true"
        />

        {/* 404 */}
        <div className="dash-nf-404">
          <span
            className="font-black leading-none tracking-tight"
            style={{
              fontSize: 'clamp(5rem,18vw,10rem)',
              color: 'var(--color-accent)',
              opacity: 0.9,
            }}
            aria-hidden="true"
          >
            404
          </span>
        </div>

        {/* Überschrift */}
        <div className="dash-nf-headline flex items-center gap-3">
          <div
            aria-hidden="true"
            style={{
              width: '3px',
              height: '1.5rem',
              background: 'var(--color-accent)',
              flexShrink: 0,
            }}
          />
          <h1
            className="text-lg font-black uppercase tracking-widest"
            style={{ color: 'var(--color-ink)' }}
          >
            Seite nicht gefunden
          </h1>
        </div>

        {/* Beschreibung */}
        <p
          className="dash-nf-body max-w-sm text-sm leading-relaxed"
          style={{ color: 'var(--color-ink-muted)' }}
        >
          Diese Seite existiert nicht oder wurde verschoben.
        </p>

        {/* CTA */}
        <div className="dash-nf-cta">
          <Link
            to="/"
            className="dash-nf-btn inline-flex items-center gap-2 px-6 py-2.5 text-xs font-black uppercase tracking-widest text-white no-underline"
            style={{ background: 'var(--color-accent)' }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLAnchorElement).style.background = 'var(--color-accent-dark)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLAnchorElement).style.background = 'var(--color-accent)';
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              className="h-3.5 w-3.5"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M9.293 2.293a1 1 0 011.414 0l7 7A1 1 0 0117 11h-1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-3a1 1 0 00-1-1H9a1 1 0 00-1 1v3a1 1 0 01-1 1H5a1 1 0 01-1-1v-6H3a1 1 0 01-.707-1.707l7-7z"
                clipRule="evenodd"
              />
            </svg>
            Zurück zum Dashboard
          </Link>
        </div>
      </div>
    </>
  );
}

/**
 * Root-Route — minimaler Wrapper ohne Layout.
 * Das Dashboard-Layout mit Sidebar lebt in _authenticated.tsx.
 * Die Sign-In-Seite bekommt dadurch ihr eigenes zentriertes Full-Screen Layout.
 * Die notFoundComponent greift bei allen unbekannten Routen.
 */
export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
  notFoundComponent: NotFoundPage,
});

function RootLayout() {
  return (
    <div className="flex min-h-screen w-full bg-paper text-ink antialiased">
      <Outlet />
    </div>
  );
}
